/**
 * Pure latch/resolve policy for LaunchDarkly context identification.
 * Owned by useLaunchDarklyContext; route guards stay latch-free and only share
 * the "require atlas userId" gate via buildLDContextFromAuth.
 */

type LDLatchScope = {
  userId?: string;
  email?: string;
  name?: string;
  organizationId?: string;
  facilityId?: string;
  /** Facility URL slug (LD `agent.key`). Not the agent UUID. */
  facilityUrlName?: string;
};

export type LDContextLatch = LDLatchScope & {
  sub: string;
};

export type LDLatchCurrentInputs = LDLatchScope & {
  prior: LDContextLatch | null;
  isAuthenticated: boolean;
  sub: string | undefined;
  routeAgent?: string;
};

export type LDLatchResolved = Omit<LDLatchScope, 'userId'> & {
  userId: string;
};

export type LDLatchResolveResult =
  | { action: 'clear' }
  | { action: 'defer'; latch: LDContextLatch | null }
  | { action: 'wait'; latch: LDContextLatch }
  | { action: 'identify'; latch: LDContextLatch; resolved: LDLatchResolved };

/**
 * Resolves whether to clear, defer (facility unresolved), wait (no userId), or identify.
 * Current non-empty values win; latch fills gaps for the same Auth0 sub only.
 */
export function resolveLDContextLatch(input: LDLatchCurrentInputs): LDLatchResolveResult {
  const {
    isAuthenticated,
    sub,
    userId,
    email,
    name,
    organizationId,
    facilityId,
    facilityUrlName,
    routeAgent,
  } = input;

  if (!isAuthenticated || !sub) {
    return { action: 'clear' };
  }

  // Different sub — ignore prior so one user's data never rides to the next.
  const latch = input.prior?.sub === sub ? input.prior : null;

  // Facility still resolving for this URL — wait. Do not identify with a latched/stale facility.
  if (routeAgent && routeAgent !== facilityUrlName) {
    if (userId) {
      return {
        action: 'defer',
        latch: {
          sub,
          userId,
          email: email ?? latch?.email,
          name: name ?? latch?.name,
          organizationId,
          // Drop facility scope so a later fill cannot revive the previous facility.
          facilityId: undefined,
          facilityUrlName: undefined,
        },
      };
    }
    return { action: 'defer', latch };
  }

  const resolved = {
    userId: userId ?? latch?.userId,
    email: email ?? latch?.email,
    name: name ?? latch?.name,
    organizationId: organizationId ?? latch?.organizationId,
    facilityId: facilityId ?? latch?.facilityId,
    facilityUrlName: facilityUrlName ?? latch?.facilityUrlName,
  };

  const nextLatch: LDContextLatch = { sub, ...resolved };

  if (!resolved.userId) {
    return { action: 'wait', latch: nextLatch };
  }

  return {
    action: 'identify',
    latch: nextLatch,
    resolved: { ...resolved, userId: resolved.userId },
  };
}
